import React, { useState, useRef, useEffect } from "react";
import "./VideoOverlayMenu.css";
import { copyToClipboard } from "../helpers/globals";
import { getVideoUrl } from "../utils/urlHelpers";
import { toggleBookmark } from "../service/auth";
import thumbsdownEmoji from "../assets/thumbsdown-emoji.png";
import playIcon from "../assets/play-icon.png";
import pauseIcon from "../assets/pause-icon.png";
import skipIcon from "../assets/skip-icon.png";
import fullscreenIcon from "../assets/fullscreen-icon.png";

const VideoOverlayMenu = ({
  // Player reference
  playerRef,

  // Video info
  videoId,
  videoUUID,
  videoTitle,
  videoArtist,

  // State
  isLiked = false,
  isPlaying = false,
  isFullscreen = false,
  isAdPlaying = false,

  // Handlers
  onLike,
  onSkip,
  onPlayPause,
  onFullscreen,

  // Toggle controls
  showThumbsdown = true,
  showPlayPause = true,
  showFullscreen = true,
  showSkip = true,
  showBookmark = true,
  showCopyLink = true,

  // Configuration
  visible = false,
  alwaysVisible = false,
  autoHideDelay = 4000, // Auto-hide after 4 seconds by default, 0 to disable
  isMobile = false,
}) => {
  const starBtnRef = useRef(null);
  const autoHideTimerRef = useRef(null);
  const [showCopiedFeedback, setShowCopiedFeedback] = useState(false);
  const [showStarAnimation, setShowStarAnimation] = useState(false);
  const [animationStyle, setAnimationStyle] = useState({});
  const [showThumbsdownOverlay, setShowThumbsdownOverlay] = useState(false);
  const [showStarOverlay, setShowStarOverlay] = useState(false);
  const [isBookmarked, setIsBookmarked] = useState(isLiked);
  const [isBookmarkLoading, setIsBookmarkLoading] = useState(false);
  const [isAutoHidden, setIsAutoHidden] = useState(false);

  // Sync with prop when it changes (e.g., when video changes)
  useEffect(() => {
    setIsBookmarked(isLiked);
  }, [isLiked, videoId]);

  // Auto-hide timer logic
  useEffect(() => {
    // Clear any existing timer
    if (autoHideTimerRef.current) {
      clearTimeout(autoHideTimerRef.current);
      autoHideTimerRef.current = null;
    }

    // If alwaysVisible or no autoHideDelay, don't auto-hide
    if (alwaysVisible || autoHideDelay <= 0) {
      setIsAutoHidden(false);
      return;
    }

    // If visible, reset auto-hidden and start timer
    if (visible) {
      setIsAutoHidden(false);
      autoHideTimerRef.current = setTimeout(() => {
        setIsAutoHidden(true);
      }, autoHideDelay);
    }

    return () => {
      if (autoHideTimerRef.current) {
        clearTimeout(autoHideTimerRef.current);
      }
    };
  }, [visible, alwaysVisible, autoHideDelay]);

  // Hide controls when ads are playing
  useEffect(() => {
    if (isAdPlaying) {
      setIsAutoHidden(true);
      if (autoHideTimerRef.current) {
        clearTimeout(autoHideTimerRef.current);
      }
    }
  }, [isAdPlaying]);

  // Show controls when video is paused
  useEffect(() => {
    if (!isPlaying && !isAdPlaying) {
      setIsAutoHidden(false);
      if (autoHideTimerRef.current) {
        clearTimeout(autoHideTimerRef.current);
      }
    }
  }, [isPlaying, isAdPlaying]);

  // Handle fullscreen changes
  useEffect(() => {
    if (isFullscreen && !isAdPlaying) {
      setIsAutoHidden(false);
    }
  }, [isFullscreen, isAdPlaying]);

  // Compute final visibility
  const isVisible = alwaysVisible || (visible && !isAutoHidden);

  // Don't render overlay at all when ads are playing
  if (isAdPlaying) {
    return null;
  }

  // Handle copy link
  const handleCopyLink = (e) => {
    e.stopPropagation();
    const videoPath = getVideoUrl({ artist: videoArtist, title: videoTitle, id: videoId });
    const videoUrl = `${window.location.protocol}//${window.location.host}${videoPath}`;
    copyToClipboard(videoUrl);

    setShowCopiedFeedback(true);
    setTimeout(() => setShowCopiedFeedback(false), 2000);
  };

  // Handle star click with animation and API call
  const handleStarClick = async (e) => {
    e.stopPropagation();

    if (isBookmarkLoading) return;

    const wasBookmarked = isBookmarked;
    const authToken = localStorage.getItem("authToken");

    // Show animation for adding bookmark (not removing)
    if (!wasBookmarked) {
      // Show star emoji overlay
      setShowStarOverlay(true);
      setTimeout(() => setShowStarOverlay(false), 800);

      // Get the star button position for flying animation
      const starBtn = starBtnRef.current;
      if (starBtn) {
        const rect = starBtn.getBoundingClientRect();
        setAnimationStyle({
          '--start-x': `${rect.left + rect.width / 2}px`,
          '--start-y': `${rect.top + rect.height / 2}px`,
        });

        setShowStarAnimation(true);

        // Trigger emoji bounce after 0.2s (when star reaches the emoji)
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('starAddedToLibrary'));
        }, 200);

        setTimeout(() => {
          setShowStarAnimation(false);
        }, 800);
      }
    }

    // Optimistic update
    setIsBookmarked(!wasBookmarked);

    // Call API if authenticated
    if (authToken && videoId) {
      setIsBookmarkLoading(true);
      try {
        const response = await toggleBookmark(videoId);
        const bookmarked = response.data?.data?.bookmarked === 1;
        setIsBookmarked(bookmarked);
      } catch (error) {
        console.error("Failed to toggle bookmark:", error);
        // Revert on error
        setIsBookmarked(wasBookmarked);
      } finally {
        setIsBookmarkLoading(false);
      }
    }

    // Call parent handler if provided
    if (onLike) {
      onLike(videoId);
    }
  };

  // Handle thumbsdown/skip
  const handleThumbsdown = (e) => {
    e.stopPropagation();

    setShowThumbsdownOverlay(true);

    setTimeout(() => {
      setShowThumbsdownOverlay(false);
      if (onSkip) {
        onSkip();
      }
    }, 800);
  };

  // Wrapper for action handlers - auto-hide after action
  const handleAction = (actionHandler) => (e) => {
    e.stopPropagation();
    if (actionHandler) {
      actionHandler(e);
    }
    setIsAutoHidden(true);
    if (autoHideTimerRef.current) {
      clearTimeout(autoHideTimerRef.current);
    }
  };

  const hasRightSideButtons = showBookmark || showCopyLink || showThumbsdown;
  const hasCenterControls = showPlayPause || showFullscreen || showSkip;

  return (
    <>
      {/* Main Overlay Container */}
      <div className={`video-overlay-menu ${isVisible ? 'visible' : ''} ${alwaysVisible ? 'always-visible' : ''}`}>

        {/* Center Controls (Play/Pause, Fullscreen, Skip) */}
        {hasCenterControls && (
          <div className="video-overlay-center-controls">
            {showPlayPause && onPlayPause && (
              <button
                className="video-overlay-center-btn video-overlay-center-btn--play-pause"
                onClick={handleAction(onPlayPause)}
                aria-label={isPlaying ? "Pause" : "Play"}
              >
                <img
                  src={isPlaying ? pauseIcon : playIcon}
                  alt={isPlaying ? "Pause" : "Play"}
                  className="video-overlay-center-btn-icon"
                />
              </button>
            )}

            {showFullscreen && onFullscreen && (
              <button
                className="video-overlay-center-btn video-overlay-center-btn--fullscreen"
                onClick={handleAction(onFullscreen)}
                aria-label={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
              >
                <img
                  src={fullscreenIcon}
                  alt={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
                  className="video-overlay-center-btn-icon"
                />
              </button>
            )}

            {showSkip && onSkip && (
              <button
                className="video-overlay-center-btn video-overlay-center-btn--skip"
                onClick={handleAction(onSkip)}
                aria-label="Skip to next"
              >
                <img
                  src={skipIcon}
                  alt="Skip"
                  className="video-overlay-center-btn-icon"
                />
              </button>
            )}
          </div>
        )}

        {/* Right Side Menu (Star/Link/Thumbsdown) */}
        {hasRightSideButtons && (
          <div className="video-overlay-side-menu">
            {showBookmark && (
              <button
                ref={starBtnRef}
                className={`video-overlay-btn like-btn ${isBookmarked ? 'active' : ''} ${isBookmarkLoading ? 'loading' : ''}`}
                onClick={handleStarClick}
                title={isBookmarked ? "Remove from library" : "Save to library"}
                disabled={isBookmarkLoading}
              >
                <svg viewBox="0 0 24 24" fill={isBookmarked ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2">
                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                </svg>
              </button>
            )}

            {showCopyLink && (
              <button
                className={`video-overlay-btn link-btn ${showCopiedFeedback ? 'copied' : ''}`}
                onClick={handleCopyLink}
                title="Copy link to share"
              >
                {showCopiedFeedback ? (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                  </svg>
                )}
              </button>
            )}

            {showThumbsdown && (
              <button
                className="video-overlay-btn dislike-btn"
                onClick={handleThumbsdown}
                title="Not for me"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3zm7-13h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17" />
                </svg>
              </button>
            )}
          </div>
        )}
      </div>

      {/* Copied Link Feedback Overlay */}
      {showCopiedFeedback && (
        <div className="video-overlay-copied">
          <span className="video-overlay-copied-text">Copied</span>
        </div>
      )}

      {/* Star Overlay */}
      {showStarOverlay && (
        <div className="video-overlay-star">
          <span className="video-overlay-star-emoji">⭐</span>
        </div>
      )}

      {/* Thumbs Down Overlay */}
      {showThumbsdownOverlay && (
        <div className="video-overlay-thumbsdown">
          <img src={thumbsdownEmoji} alt="Thumbs down" className="video-overlay-thumbsdown-emoji" />
        </div>
      )}

      {/* Flying Star Animation */}
      {showStarAnimation && (
        <div className="video-overlay-flying-star-container" style={animationStyle}>
          <div className="video-overlay-flying-star">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
            </svg>
          </div>
          <div className="sparkle sparkle-1"></div>
          <div className="sparkle sparkle-2"></div>
          <div className="sparkle sparkle-3"></div>
          <div className="sparkle sparkle-4"></div>
          <div className="sparkle sparkle-5"></div>
        </div>
      )}
    </>
  );
};

export default VideoOverlayMenu;
